import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "lodash-es";
import Dropzone from "react-dropzone";
import { CheckCircle, ChevronDown, Download, ChevronLeft, ChevronRight, CircleCheck, CircleX, ExternalLink, LoaderCircle, Info, InfoIcon, Pencil, Plus, RefreshCcw, RotateCcw, Save, Trash2, TriangleAlert, X } from "lucide-react";
import imageCompression from 'browser-image-compression';
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import { AppStateContext } from "@/state/AppStateContext";
import { addCustomGPTAccess, checkCustomGPT, deleteCustomGPT, generateCustomGPTSystemPrompt, getCustomGPT, getCustomGPTAccess, getCustomGPTIndexedFiles, getCustomGPTStatus, grantCustomGPTAccess, resyncCustomGPT, saveCustomGPT } from "@/api/api";
import type { SharepointLibraryStatus, StreamErrorDetails } from "@/api/models";
import { getFileIcon } from '@/utils/fileValidation';
import { getSafeSharePointUrl } from "@/utils/safeUrls";
import { FileSnapshotError, snapshotFiles } from '@/utils/fileSnapshot';
import ErrorDetails from "@/components/Message/ErrorDetails";
import { getErrorDetails, getErrorSummary } from "@/utils/errorMessages";
import { categories } from "@/constants/categories";
import { chatModels } from "@/constants/models";
import { refreshUserCustomGPTs, setTitle } from "@/utils/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import UsersPicker from "./UsersPicker";

type IndexedFile = { filepath: string; size: number | null };

/**
 * Render a byte count as a short human-readable size
**/
const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
};

/**
 * Plain-language explanation of why the nightly sync disabled a SharePoint
 * Document Library, for a non-technical Custom GPT owner. Unknown/future
 * reason codes fall back to a generic message rather than surfacing the
 * raw code.
**/
const getSharepointSyncDisabledMessage = (reason: string, detail: string): string => {
  switch (reason) {
    case 'not_a_document_library':
      return "This URL isn't a SharePoint document library, so nothing from it was added. Point it at a document library, then update.";
    case 'nested_under_other_library':
      return detail
        ? `This folder is already covered by another synced library (${detail}), so it isn't indexed separately. It can be removed.`
        : "This folder is already covered by another synced library, so it isn't indexed separately. It can be removed.";
    case 'folder_gone':
      return "This library or folder no longer exists in SharePoint. Update or remove the URL, then update.";
    case 'invalid_url':
      return "This URL couldn't be read as a SharePoint document library. Check the URL, then update.";
    default:
      return "This library isn't being synced. Check the URL, then update.";
  }
}

const EditCustomGPT = () => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const { gptId } = useParams();
  const ADMINS = appStateContext?.state.frontendSettings?.admins || [];
  const ENV = appStateContext?.state?.frontendSettings?.display_env || '';
  const CREATOR_GROUP = appStateContext?.state.frontendSettings?.creator_group || '';
  const handleRefreshUserCustomGPTs = () => refreshUserCustomGPTs(appStateContext);
  const [knowledgeFilesDialogOpen, setKnowledgeFilesDialogOpen] = useState<boolean>(false);
  const [knowledgeSPDocLibDialogOpen, setKnowledgeSPDocLibDialogOpen] = useState<boolean>(false);
  const [knowledgeSPArticlesDialogOpen, setKnowledgeSPArticlesDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [image, setImage] = useState<File | null>(null);
  const [customGPTID, setCustomGPTID] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [isGeneratingSystemPrompt, setIsGeneratingSystemPrompt] = useState<boolean>(false);
  const [instructions, setInstructions] = useState<string>('');
  const [coCreator, setCoCreator] = useState<string>('');
  const [coCreatorName, setCoCreatorName] = useState<string>('');
  const [convStarter1, setConvStarter1] = useState<string>('');
  const [convStarter2, setConvStarter2] = useState<string>('');
  const [convStarter3, setConvStarter3] = useState<string>('');
  const [convStarter4, setConvStarter4] = useState<string>('');
  const [model, setModel] = useState<string>((chatModels.find(m => m.default) || chatModels[0]).id);
  const [files, setFiles] = useState<File[] | []>([]);
  const filesRef = useRef<File[]>(files);
  const pendingKnowledgePreparationCountRef = useRef(0);
  const knowledgeSelectionRevisionRef = useRef(0);
  const [isPreparingKnowledgeFiles, setIsPreparingKnowledgeFiles] = useState(false);
  const isMountedRef = useRef(true);
  const isSavingRef = useRef(false);
  const [totalSize, setTotalSize] = useState<number>(0);
  const [existingStorageBytes, setExistingStorageBytes] = useState<number>(0);
  const STORAGE_LIMIT_BYTES = 5 * 1024 * 1024 * 1024;
  const [filesArr, setFilesArr] = useState<string[]>([]);
  const [toDeleteArr, setToDeleteArr] = useState<string[]>([]);
  const [sharepointURLs, setSharepointURLs] = useState<string[]>([]);
  const [sharepointStatus, setSharepointStatus] = useState<SharepointLibraryStatus[]>([]);
  const [currentSPDocLibURL, setCurrentSPDocLibURL] = useState<string>('');
  const [editingSPDocLibIndex, setEditingSPDocLibIndex] = useState<number | null>(null);
  const [currentArticleURL, setCurrentArticleURL] = useState<string>('');
  const [sharepointArticles, setSharepointArticles] = useState<string[]>([]);
  const [currentWebsiteURL, setCurrentWebsiteURL] = useState<string>('');
  const [websites, setWebsites] = useState<string[]>([]);
  const [topN, setTopN] = useState<string>('10');
  const [filesDownloadable, setFilesDownloadable] = useState<boolean>(true);
  const [visibility, setVisibility] = useState<number>(2);
  const [accessSource, setAccessSource] = useState<number>(1);
  const [isIndexing, setIsIndexing] = useState<boolean>(false);
  const [isReady, setIsReady] = useState<boolean>(true);
  const [accessArr, setAccessArr] = useState<any>([]);
  const [convStartersExpanded, setConvStartersExpanded] = useState<boolean>(false);
  const [optionsExpanded, setOptionsExpanded] = useState<boolean>(false);
  const [filesExpanded, setFilesExpanded] = useState<boolean>(false);
  const [spDocLibExpanded,setSPDocLibExpanded] = useState<boolean>(false);
  const [spArticlesExpanded, setSPArticlesExpanded] = useState<boolean>(false);
  const [errors, setErrors] = useState<{ name?: string; description?: string; files?: string; sharepoint?: string; article?: string, website?: string }>({});
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveErrorDetails, setSaveErrorDetails] = useState<StreamErrorDetails | null>(null);
  const [saveErrorSeverity, setSaveErrorSeverity] = useState<'warning' | 'error' | null>(null);
  // A partial save is not a failure, so it must not render as one.
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  const [canClose, setCanClose] = useState<boolean>(true);
  const [similar, setSimilar] = useState<any>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [hoursWarning, setHoursWarning] = useState<boolean>(false);
  const [isReloading, setIsReloading] = useState<boolean>(false);
  const [finished, setFinished] = useState<boolean>(false);
  const [loadingIndexedFiles, setLoadingIndexedFiles] = useState<boolean>(false);
  const [indexedFiles, setIndexedFiles] = useState<IndexedFile[]>([]);
  const [indexedFilesDialogOpen, setIndexedFilesDialogOpen] = useState<boolean>(false);
  const indexingIntervalRef = useRef<number | null>(null);
  // Whether this Custom GPT was still being created when the page loaded. Creation
  // progress is only worth reporting in that case — one that loaded ready has
  // nothing to announce, so opening it must not flash "Created".
  const loadedMidCreationRef = useRef<boolean>(false);

  const fileTypes = {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.template': ['.xltx'],
    'application/vnd.ms-excel.template.macroEnabled.12': ['.xltm'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/vnd.ms-outlook': ['.msg'],
    'image/jpeg': ['.jpeg', '.jpg'],
    'image/png': ['.png'],
    'text/plain': ['.txt', '.m', '.toml', '.sh', '.bash', '.ps1', '.sql', '.java', '.cpp', '.r', '.log', '.ini', '.cfg', '.conf', '.properties', '.c', '.h', '.hpp', '.cs', '.v', '.sv', '.vhdl', '.tmdl'],
    'text/html': ['.htm', '.html'],
    'text/csv': ['.csv'],
    'text/tab-separated-values': ['.tsv'],
    'application/json': ['.json', '.ipynb', '.bim'],
    'application/xml': ['.xml', '.xsd', '.bpmn'],
    'text/markdown': ['.md'],
    'text/x-python': ['.py'],
    'text/x-matlab': ['.m'],
    'application/x-yaml': ['.yml', '.yaml'],
    'application/javascript': ['.js'],
    'application/typescript': ['.ts'],
    'message/rfc822': ['.eml'],
    'application/x-matlab-data': ['.mat'],
    'application/octet-stream': ['.pbit'],
  };

  /** 
   * Handle checking user has access to this page
  **/
  useEffect(() => {
    if ((!appStateContext?.state.userGroups.includes(CREATOR_GROUP) && isEmpty(gptId) && ENV !== 'DEV')) navigate('/spaces');
  }, [appStateContext?.state.userGroups, gptId]);

  useEffect(() => {
    isMountedRef.current = true;
    const selectionRevision = knowledgeSelectionRevisionRef;
    return () => {
      isMountedRef.current = false;
      selectionRevision.current++;
      if (indexingIntervalRef.current !== null) {
        window.clearInterval(indexingIntervalRef.current);
        indexingIntervalRef.current = null;
      }
    };
  }, []);

  /** 
   * Handle loading Custom GPT
  **/
  useEffect(() => {
    if (isEmpty(gptId)) setTitle(appStateContext, 'New Space');
    if (!isEmpty(appStateContext?.state.currentUser) && !isEmpty(gptId)) loadCustomGPT();
  }, [appStateContext?.state.currentUser, gptId]);

  /**
   * Handle loading current knowledge storage usage for the upload pre-check
  **/
  useEffect(() => {
    if (!isEmpty(gptId)) checkReady();
  }, [gptId]);

  /** 
   * Handle checking if Custom GPT is ready
  **/
  useEffect(() => {
    // If Custom GPT is loaded and not ready
    let intervalId: number | null = null;
    if (!isEmpty(appStateContext?.state.currentUser) && !isEmpty(gptId) && !isReady) {
      // Set interval to check readiness every 15 seconds
      intervalId = window.setInterval(() => {
        checkReady();
      }, 15000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [appStateContext?.state.currentUser, gptId, isReady]);
  
  /** 
   * Handle compressing upload icon image
   * 
   * @param {File} imageFile - The icon image file
  **/
  const compressImage = async (imageFile: File) => {
    const options = {
      maxSizeMB: 1,
      maxWidthOrHeight: 500,
      useWebWorker: true,
    };

    try {
      return await imageCompression(imageFile, options);
    } catch (error) {
      console.error('Error compressing image:', error);
      return imageFile;
    }
  }

  /** 
   * Handle uploading & compressing of icon image
   * 
   * @param {React.ChangeEvent<HTMLInputElement>} event - The file upload event
  **/
  const handleIconChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    // Get files
    const files = event.target.files;
    if (files && files.length > 0) {
      const selectedFile = files[0];

      // Check file size, if greater than 5MB, reject & alert user
      if (selectedFile.size > 5 * 1024 * 1024) {
        alert('File size exceeds 5MB limit');
        return;
      }
      try {
        // Compress image
        const compressedFile = await compressImage(selectedFile);

        // Set image preview
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target) setImagePreview(e.target.result as string | null);
          setImage(compressedFile);
        };
        reader.readAsDataURL(compressedFile);
      } catch (err) {
        console.error('Error compressing image:', err);
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target) setImagePreview(e.target.result as string | null);
          setImage(selectedFile);
        };
        reader.readAsDataURL(selectedFile);
      }
    }
  }

  /** 
   * Handle updating knowledge files
   *
   * @param {File[]} nextFiles - The uploaded files
  **/
  const updateKnowledgeFiles = (nextFiles: File[]) => {
    const nextTotalSize = nextFiles.reduce((sum, file) => sum + file.size, 0);
    filesRef.current = nextFiles;
    setFiles(nextFiles);
    setTotalSize(nextTotalSize);
    setHoursWarning(nextTotalSize >= 50 * 1024 * 1024);
  }

  /** 
   * Handle knowledge file changes
   *
   * @param {File[]} acceptedFiles - The uploaded files
  **/
  const handleKnowledgeFileChange = async (acceptedFiles: File[]) => {
    if (isSavingRef.current || !acceptedFiles || acceptedFiles.length === 0) return;

    const existingFiles = new Set(filesRef.current.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
    const filesToSnapshot: File[] = [];
    const oversizedFiles: string[] = [];

    acceptedFiles.forEach((file) => {
      if (file.size > 250 * 1024 * 1024) {
        oversizedFiles.push(file.name);
        return;
      }
      const fileKey = `${file.name}:${file.size}:${file.lastModified}`;
      if (!existingFiles.has(fileKey)) {
        existingFiles.add(fileKey);
        filesToSnapshot.push(file);
      }
    });

    if (oversizedFiles.length > 0) alert(`The following file(s) exceed the 250MB limit and were not added:\n${oversizedFiles.join('\n')}`);

    if (filesToSnapshot.length === 0) return;

    const selectionRevision = knowledgeSelectionRevisionRef.current;
    pendingKnowledgePreparationCountRef.current++;
    setIsPreparingKnowledgeFiles(true);
    try {
      const snapshots = await snapshotFiles(filesToSnapshot);
      if (!isMountedRef.current || selectionRevision !== knowledgeSelectionRevisionRef.current) return;

      // Recheck against the latest state after asynchronous reads so overlapping
      // picker/drop selections merge without overwriting one another.
      const latestFiles = filesRef.current;
      const latestKeys = new Set(latestFiles.map((file) => `${file.name}:${file.size}:${file.lastModified}`));
      const additions = snapshots.filter((file) => {
        const key = `${file.name}:${file.size}:${file.lastModified}`;
        if (latestKeys.has(key)) return false;
        latestKeys.add(key);
        return true;
      });
      updateKnowledgeFiles([...latestFiles, ...additions]);
    } catch (error) {
      console.error('Could not prepare selected knowledge files:', error);
      if (isMountedRef.current) alert(error instanceof FileSnapshotError ? error.message : 'Could not prepare selected files. Please select them again.');
    } finally {
      pendingKnowledgePreparationCountRef.current = Math.max(0, pendingKnowledgePreparationCountRef.current - 1);
      if (isMountedRef.current) setIsPreparingKnowledgeFiles(pendingKnowledgePreparationCountRef.current > 0);
    }
  }

  /** 
   * Handle removing file from array to be uploaded
   * 
   * @param {File} file - The filename to be removed
  **/
  const handleRemoveFile = (file: File) => {
    knowledgeSelectionRevisionRef.current++;
    updateKnowledgeFiles(filesRef.current.filter(f => f !== file));
  }

  /** 
   * Handle validating that the required form fields are filled
  **/
  const validateInputs = () => {
    const lErrors: { name?: string; description?: string; files?: string; sharepoint?: string; article?: string, website?: string } = {};
    const urlPattern = /^https:\/\/smsgroupgmbh\.sharepoint\.com(?:\/[^\/]*)*\/(sites|teams)\/(?!intranet-content)([^\/]?.*)?$/i;
    if (name.trim() === '') lErrors.name = 'Name is required';
    if (description.trim() === '') lErrors.description = 'Description is required';
    sharepointURLs.forEach((url: string) => {
      if (url.trim() !== '' && !getSafeSharePointUrl(url.trim())) lErrors.sharepoint = 'SharePoint URL must be within tenant: https://smsgroupgmbh.sharepoint.com/';
      else if (url.trim() !== '' && !urlPattern.test(url.trim())) lErrors.sharepoint = 'SharePoint Document Library must be a site or team document library';
    });
    sharepointArticles.forEach((article: string, index: number) => {
      if (article.trim() !== '' && !getSafeSharePointUrl(article.trim())) lErrors.article = `SharePoint Intranet Article ${index + 1} must be within tenant: https://smsgroupgmbh.sharepoint.com/`;
    });
    websites.forEach((website: string, index: number) => {
      if (website.trim() !== '' && website.trim().startsWith('https://smsgroupgmbh.sharepoint.com/')) lErrors.website = `Website ${index + 1} should be a public website, NOT within tenant: https://smsgroupgmbh.sharepoint.com/`;
    });
    setErrors(lErrors);
    return lErrors;
  }

  /** 
   * Handle getting details for the selected Custom GPT
  **/
  const loadCustomGPT = (idOverride?: string) => {
    setIsLoading(true);
    // idOverride covers the case where the record exists server-side but the
    // route has no gptId yet — a create that was rejected after the document
    // was written.
    getCustomGPT(idOverride || gptId || '').then((res) => {
      // If Custom GPT found
      if (res && res['customGPT'] && res['customGPT'].length > 0) {
        let customGPT = res['customGPT'][0];

        // Set document title
        setTitle(appStateContext, customGPT.name);

        // Confirm Custom GPT belongs to current user, if not redirect away
        if (customGPT.createdBy != appStateContext?.state.currentUser?.id && customGPT.coCreator != appStateContext?.state.currentUser?.id && !ADMINS.includes(appStateContext?.state.currentUser?.id || '')) navigate('/spaces');

        // Set values
        setCustomGPTID(customGPT.id);
        if (!isEmpty(customGPT.filename)) setImagePreview(`https://asappsmsstore.blob.core.windows.net/customgpticons/${customGPT.filename}`);
        setName(customGPT.name);
        setDescription(customGPT.description);
        setCategory(customGPT.category || '');
        setInstructions(customGPT.systemPrompt);
        setConvStarter1(customGPT.convStarter1 || '');
        setConvStarter2(customGPT.convStarter2 || '');
        setConvStarter3(customGPT.convStarter3 || '');
        setConvStarter4(customGPT.convStarter4 || '');
        setModel(customGPT.model || (chatModels.find(m => m.default) || chatModels[0]).id);
        setTopN(customGPT.topN || '10');
        setFilesDownloadable(customGPT.filesDownloadable == '0' ? false : true);
        setSharepointURLs(customGPT.sharepointURLs || (customGPT.sharepointURL ? [customGPT.sharepointURL] : []));
        setSharepointStatus(Array.isArray(res['sharepointStatus']) ? res['sharepointStatus'] : []);
        setSharepointArticles((customGPT.sharepointArticles && customGPT.sharepointArticles != '') ? customGPT.sharepointArticles.split('|') : []);
        setVisibility(customGPT.visibility || 0);
        setCoCreator(customGPT.coCreator || '');
        setCoCreatorName(customGPT.coCreatorName || '');
        setIsIndexing(customGPT.indexing || false);
        const ready = customGPT.ready == 'true';
        loadedMidCreationRef.current = !ready;
        setIsReady(ready);

        // Set files array
        if (res && res['files'] && res['files'].length > 0) setFilesArr(res['files'].map((file: any) => file['filename']));
        else setFilesArr([]);

        // Set access array
        if (res && res['access'] && res['access'].length > 0) setAccessArr(res['access'].map((access: any) => access));
        else setAccessArr([]);
      }
      // Otherwise, navigate away
      else navigate('/spaces');
      
      setIsLoading(false);
    }).catch((err) => {
      navigate('/spaces');
    });
  }

  /** 
   * Handle checking if Custom GPT is ready
  **/
  const checkReady = () => {
    getCustomGPTStatus(gptId || '').then((res) => {
      // If Custom GPT found
      if (res && res.length > 0) {
        let customGPT = res[0];

        // If finished creating. Only announce it when the page loaded mid-creation:
        // this also runs once on open purely to pick up storage/indexing state, and
        // an already-ready Custom GPT was not created by this visit.
        if (customGPT.ready == 'true') {
          setIsReady(true);
          if (loadedMidCreationRef.current) {
            setFinished(true);
            handleRefreshUserCustomGPTs();
          }
        }
        setIsIndexing(customGPT.indexing ?? false);
        setExistingStorageBytes(customGPT.storageBytes ?? 0);
      }
    });
  }

  /**
   * Handle checking if Custom GPT is indexed
  **/
  const checkIndexed = () => {
    getCustomGPTStatus(gptId || '').then((res) => {
      // If Custom GPT found
      if (res && res.length > 0) {
        let customGPT = res[0];

        // Update indexing status
        const indexing = customGPT.indexing ?? false;
        setIsIndexing(indexing);
        if (!indexing && indexingIntervalRef.current !== null) {
          window.clearInterval(indexingIntervalRef.current);
          indexingIntervalRef.current = null;
        }
      }
    });
  }

  /** 
   * Handle triggering resyncing the knowledge files
  **/
  const resyncKnowledgeFiles = async () => {
    if (confirm("Resync ALL knowledge documents?\n\nEverything is re-indexed from scratch, which can take some time. If this Space holds more than 5GB, the files that no longer fit are removed — the oldest are kept, and you will be emailed a list of anything dropped.")) {
      setIsIndexing(true);
      setSaveError(null);
      setSaveErrorDetails(null);
      setSaveErrorSeverity(null);
      setSaveWarning(null);

      const res = await resyncCustomGPT(gptId || '');

      // A successful trigger re-enables every disabled library server-side (per the
      // backend contract), so the sync-disabled warnings above go stale the instant
      // this succeeds. Clear them optimistically rather than waiting on a refetch —
      // that's honest here, not a lie: if a library is still bad, the next nightly
      // sync will re-disable it and the warning will return then.
      if (res && res.ok) setSharepointStatus([]);

      // A refusal is not the same as a failure. 409 means a run really is in progress
      // and 503 means the server could not tell — in both cases indexing may well still
      // be running, and `isIndexing` is the only thing gating the knowledge editors and
      // the "Processing…" spinner. Clearing it on those unlocked the form mid-run and
      // nothing re-locked it: the readiness poll only runs while the Custom GPT is not
      // ready, so an established one stayed wrong until a reload. Keep the lock and let
      // the poll below resolve it.
      if (res && !res.ok) {
        const body = await res.json().catch(() => null);
        const rawError = body?.error ?? 'Could not start the resync. Please try again.';
        setSaveErrorDetails(getErrorDetails(rawError, body?.error_details, res.status));
        setSaveError(res.status === 409
          ? 'Indexing is currently in progress. Please wait until processing completes.'
          : getErrorSummary(rawError, body?.error_details));
        setSaveErrorSeverity(res.status === 409 || res.status === 503 ? 'warning' : 'error');
        if (res.status !== 409 && res.status !== 503) {
          setIsIndexing(false);
          return;
        }
      }

      if (indexingIntervalRef.current !== null) window.clearInterval(indexingIntervalRef.current);
      // Set interval to check readiness every 15 seconds
      indexingIntervalRef.current = window.setInterval(() => {
        checkIndexed();
      }, 15000);
    }
  }

  /** 
   * Handle generating system prompt
  **/
  const generateSystemPrompt = () => {
    if (!isEmpty(name) && !isEmpty(description) && isEmpty(instructions)) {
      setIsGeneratingSystemPrompt(true);
      generateCustomGPTSystemPrompt(name || '', description || '').then((res) => {
        if (res && res.system_prompt) setInstructions(res.system_prompt);
        setIsGeneratingSystemPrompt(false);
      });
    }
  }

  /** 
   * Handle loading indexed files & showing them to the user
  **/
  const loadIndexedFiles = () => {
    if (!isEmpty(gptId)) {
      setLoadingIndexedFiles(true);
      getCustomGPTIndexedFiles(gptId || '').then((res) => {
        setIndexedFiles(Array.isArray(res) ? res : []);
        setIndexedFilesDialogOpen(true);
        setLoadingIndexedFiles(false);
      });
    }
  }

  /** 
   * Handle exporting the indexed files list to an Excel workbook
  **/
  const exportIndexedFiles = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Indexed Files");
      worksheet.columns = [
        { header: "File", key: "filepath", width: 80 },
        { header: "Size (bytes)", key: "size", width: 16 },
        { header: "Size", key: "sizeLabel", width: 12 },
      ];
      worksheet.getRow(1).font = { bold: true };
      indexedFiles.forEach((file) => {
        worksheet.addRow({
          filepath: file.filepath,
          size: file.size ?? "",
          sizeLabel: file.size != null ? formatBytes(file.size) : "",
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      saveAs(blob, `${name || 'space'}_indexed_files.xlsx`);
    } catch (err) {
      console.error("Excel export failed:", err);
    }
  }

  /** 
   * Handle saving Custom GPT
  **/
  const handleSave = async (force: boolean = false) => {
    if (pendingKnowledgePreparationCountRef.current > 0 || (isSavingRef.current && !force)) return;
    // Check all required fields are filled
    const lErrors = validateInputs();
    
    // If all required fields are filled, create Custom GPT
    if (Object.keys(lErrors).length === 0) {
      // Set loading variable
      isSavingRef.current = true;
      setIsSaving(true);
      setCanClose(false);
      setIsChecking(false);
      setSaveError(null);
      setSaveErrorDetails(null);
      setSaveErrorSeverity(null);
      setSaveWarning(null);

      // If creating, check for similar Custom GPTs
      if (!force && isEmpty(customGPTID)) {
        setIsChecking(true);
        const similar = await checkCustomGPT(name, description);

        if (similar && similar.length > 0) {
          setSimilar(similar);
          return;
        }
        else setIsChecking(false);
      }

      // Create FormData object and append all fields to it
      const formData = new FormData();
      formData.append('id', customGPTID);
      if (image) formData.append('image', image);
      formData.append('name', name.trim());
      formData.append('description', description.trim());
      formData.append('category', category);
      formData.append('instructions', instructions.trim());
      formData.append('coCreator', coCreator);
      formData.append('coCreatorName', coCreatorName);
      formData.append('convStarter1', convStarter1.trim());
      formData.append('convStarter2', convStarter2.trim());
      formData.append('convStarter3', convStarter3.trim());
      formData.append('convStarter4', convStarter4.trim());
      formData.append('model', model);
      for (let i = 0; i < files.length; i++) formData.append('files', files[i]);
      formData.append('toDeleteFiles', JSON.stringify(toDeleteArr));
      formData.append('sharepointURLs', JSON.stringify(sharepointURLs.map(u => u.trim()).filter(Boolean)));
      formData.append('sharepointArticles', sharepointArticles.join('|'));
      formData.append('topN', topN);
      formData.append('filesDownloadable', Number(filesDownloadable).toString());
      formData.append('visibility', visibility.toString());

      // Save Custom GPT
      const saveResponse = await saveCustomGPT(formData);
      const body = await saveResponse.json().catch(() => null);
      if (saveResponse.status === 409) {
        setSaveError('Indexing is currently in progress. Please wait until processing completes before making knowledge changes.');
        setSaveErrorDetails(getErrorDetails(body?.error, body?.error_details, saveResponse.status));
        setSaveErrorSeverity('warning');
        setCanClose(true);
        return;
      }
      // The save succeeds even when some files did not fit: the ones that did are
      // indexing. Surface the server's message, which names what was left out and
      // why, and reload so the file list shows exactly what was kept rather than
      // what was submitted.
      if (!saveResponse.ok) {
        const rawError = body?.error ?? 'Save failed.';
        setSaveError(getErrorSummary(rawError, body?.error_details));
        setSaveErrorDetails(getErrorDetails(rawError, body?.error_details, saveResponse.status));
        setSaveErrorSeverity('error');
        setCanClose(true);
        return;
      }
      setCanClose(true);
      if (body?.customGPT_id) setCustomGPTID(body.customGPT_id);

      if (body?.warning) {
        setSaveWarning(body.warning);
        setToDeleteArr([]);

        // Drop the chips for files the server accepted — they are in the refreshed
        // server list now, and leaving them selected means a second Update
        // re-uploads and re-indexes them. Keep the rejected ones on screen so the
        // user is looking at exactly what did not make it.
        const rejectedNames = new Set<string>((body?.rejected ?? []).map((r: any) => r.filename));
        const stillPending = files.filter(f => rejectedNames.has(f.name));
        updateKnowledgeFiles(stillPending);

        if (body?.customGPT_id) loadCustomGPT(body.customGPT_id);
      }
      else {
        // Accepted files are now server-owned; release the browser-side copies
        // so a subsequent Update cannot accidentally upload them again.
        updateKnowledgeFiles([]);
      }

      // Reload user's Custom GPTs
      handleRefreshUserCustomGPTs();
    }
  }

  /** 
   * Handle assigning a co-creator
   * 
   * @param {any} users - The user to make a co-creator
  **/
  const handleCoCreatorChange = async (users: any) => {
    if (users && users.length > 0) {
      setCoCreator(users[0].id);
      setCoCreatorName(users[0].displayName);
    }
    else {
      setCoCreator('');
      setCoCreatorName('');
    }
  }

  /** 
   * Handle getting access for the selected Custom GPT
  **/
  const loadCustomGPTAccess = () => {
    getCustomGPTAccess(gptId || '').then((res) => {
      if (res && res && res.length > 0) setAccessArr(res.map((access: any) => access));
      else setAccessArr([]);

      setIsReloading(false);
    });
  }

  /** 
   * Handle setting a file to be deleted from Custom GPT knowledge base
   * 
   * @param {string} file - The filename to be deleted
  **/
  const handleDeleteFile = (file: string) => {
    // Confirm user wants to remove this file
    if (confirm("Are you sure that you want to delete " + file + "?")) {
      setToDeleteArr([...toDeleteArr, file]);
      setFilesArr(filesArr.filter(f => f !== file));
    }
  }

  /** 
   * Handle granting/denying/revoking Custom GPT access to a user
   * 
   * @param {string} id - The ID of the Custom GPT
   * @param {number} status - The status of the access request
  **/
  const handleGrantAccess = async (id: string, status: number, source: number=1) => {    
    if (document.getElementById(`grant_${id}`)) document.getElementById(`grant_${id}`)!.style.display = 'none';
    if (document.getElementById(`row_${id}`)) {
      if (status < 0) document.getElementById(`row_${id}`)!.style.display = 'none';
      if (source == 1) {
        grantCustomGPTAccess(id, status);
        if (status < 0) {
          const updatedAccessArr = accessArr.filter((user: any) => user.id !== id);
          setAccessArr(updatedAccessArr);
        }
      }
      else {
        await grantCustomGPTAccess(id, status);
        setIsReloading(true);
        loadCustomGPTAccess();
      }
    }
  }

  /** 
   * Handle assigning users access to Custom GPT
   * 
   * @param {any} users - The users to assign access for to the Custom GPT
  **/
  const handleUsersChange = async (users: any) => {
    if (gptId) {
      setIsReloading(true);
      const latestUser = users[users.length - 1];
      await addCustomGPTAccess(gptId, latestUser.id, latestUser.displayName, 1, accessSource);
      loadCustomGPTAccess();
    }
  }

  /** 
   * Handle deleting a Custom GPT
  **/
  const handleDelete = async () => {
    // Confirm user wants to delete this Custom GPT
    if (gptId && !isEmpty(gptId) && confirm('Are you sure you want to delete this Space?')) {
      setIsDeleting(true);
      await deleteCustomGPT(gptId);
      handleRefreshUserCustomGPTs();
      setIsDeleting(false);
      navigate('/spaces');
    }
  }

  // Disabled SharePoint Document Libraries, matched to the URL list by sharepointURL
  // (never assumed to be present or aligned with sharepointURLs).
  //
  // Scoped to URLs still configured on this Custom GPT. A mapping record outlives the
  // removal of its library — that is how a failed removal cleanup is retried — so an
  // unscoped count reports libraries that have no row to warn on, under a banner whose
  // own advice is to expand the list and fix them.
  const disabledSharepointStatuses = sharepointStatus.filter(
    s => s.syncDisabled && sharepointURLs.includes(s.sharepointURL)
  );
  const disabledSharepointCount = disabledSharepointStatuses.length;
  const actionableSharepointCount = disabledSharepointStatuses.filter(s => s.syncDisabledReason !== 'nested_under_other_library').length;

  return (
    <div className="content flex flex-col h-full bg-base-100">
      {isLoading ? 
        <div className="flex items-center justify-center h-full">
          <div className="loading-icon w-24 h-24"></div>
        </div>
      :
        <>
          <div className="flex items-center justify-between border-b border-accent ps-2 pe-4 pb-2 mt-2">
            <div className="flex items-center gap-4">
              <button type="button" className="btn btn-ghost p-0" onClick={() => navigate('/spaces')}><ChevronLeft size={24} /></button>
              <div className={`flex justify-center items-center w-[24px] h-[24px] rounded-full input ${imagePreview ? `` : `input-bordered border-dashed`} bg-transparent p-0`}>
                {imagePreview ? 
                  <img src={imagePreview} className="w-full h-full rounded-full object-cover" alt="Icon" />
                : null}
              </div>
              <div className="dark:text-white font-bold">{name || 'New Space'}</div>
            </div>
            <div className="flex items-center gap-6">
              {!isReady ? <div className="flex items-center gap-2"><span className="loading loading-spinner loading-md"></span> Creating...</div> : null}
              {finished ? <div className="flex items-center gap-2"><CircleCheck className="text-success" size={20} /> Created</div> : null}
              {isIndexing ? <div className="flex items-center text-sm gap-2"><span className="loading loading-spinner loading-sm"></span> Processing...</div> : null}
              {!isSaving && saveError ? (
                <div className="flex max-w-xs flex-col items-end text-right text-sm text-error">
                  <span>{saveError}</span>
                  {saveErrorDetails ? <ErrorDetails details={saveErrorDetails} /> : null}
                </div>
              ) : null}
              {!saveError && saveWarning ? <div className="flex items-center text-sm gap-2 text-warning max-w-xs text-right">{saveWarning}</div> : null}
              <button type="button" className="btn btn-sm btn-primary font-bold" onClick={() => handleSave(false)} disabled={isPreparingKnowledgeFiles || isSaving}><Save size={20} /> {isEmpty(gptId) ? 'Create' : 'Update'}</button>
              {(!isEmpty(gptId) && !isIndexing && (files.length > 0 || filesArr.length > 0 || sharepointURLs.length > 0)) ? <div className="flex items-center tooltip tooltip-left before:text-xs before:shadow-md before:mt-6 align-text-top" data-tip="Resync ALL knowledge documents."><button type="button" onClick={resyncKnowledgeFiles}><RotateCcw className="text-warning" size={20} /></button></div> : null}
              {!isEmpty(gptId) ? <button type="button" onClick={handleDelete}><Trash2 className="text-error" size={20} /></button> : null}
            </div>
          </div>

          <div className="w-full md:h-screen md:flex md:flex-row overflow-y-auto scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
            {/* Left Column */}
            <div className="md:flex-1 md:h-full md:overflow-y-auto border-b md:border-b-0 md:border-e border-accent scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
              <div className="w-full p-4">
                <div className="flex flex-col items-center">
                  <div className={`flex justify-center items-center w-[100px] h-[100px] rounded-full input ${imagePreview ? `` : `input-bordered border-dashed`} bg-transparent cursor-pointer p-0`} onClick={() => { if (imageInputRef.current) imageInputRef.current.click()}}>
                    {imagePreview ? 
                      <img src={imagePreview} className="w-full h-full rounded-full object-cover" alt="Icon" />
                    :
                      <div className="text-5xl text-center ms-0.5 -mt-1">+</div>
                    }
                    <input type="file" className="hidden" accept="image/*" ref={imageInputRef} onChange={handleIconChange} />
                  </div>
                </div>

                <div className="form-control w-full mb-4">
                  <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Name</span></div>
                  <input type="text" className={`input input-sm input-bordered border-secondary bg-neutral ${Object.keys(errors).indexOf('name') > -1 ? `!border-error` : ``} focus-within:outline-none shadow-sm dark:text-white w-full`} name="custom_gpt_name" placeholder="Name this Space" autoComplete="off" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                
                <div className="form-control w-full mb-4">
                  <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Description</span></div>
                  <input type="text" className={`input input-sm input-bordered border-secondary bg-neutral ${Object.keys(errors).indexOf('description') > -1 ? `!border-error` : ``} focus-within:outline-none shadow-sm dark:text-white`} placeholder="Brief description of what this Space does" autoComplete="off" maxLength={5000} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
                
                <div className="form-control w-full mb-2">
                  <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Category</span></div>
                  <Select onValueChange={setCategory} value={category}>
                    <SelectTrigger className="select-bordered border-secondary bg-neutral shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-neutral">
                      <SelectItem key={-1} value="-1"></SelectItem>
                      {categories.map((m, idx) => (<SelectItem key={idx} value={`${m.id}`}>{m.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              
                <div className="form-control w-full mb-4">
                  <div className="label pb-1">
                    <span className="label-text dark:text-white text-sm font-bold">
                      System Prompt
                      <div className=" text-black/50 dark:text-accent font-normal mt-1">The instructions that your Space will follow.</div>
                    </span>
                    <button type="button" className={`${((!isEmpty(name) && !isEmpty(description) && isEmpty(instructions)) ? `` : `hidden`)} btn btn-sm btn-outline btn-primary text-xs min-h-[1.5rem] h-[1.5rem]`} onClick={generateSystemPrompt}><RefreshCcw className={`${(isGeneratingSystemPrompt ? `animate-spin` : ``)}`} size={14} /> {isGeneratingSystemPrompt ? 'Generating' : 'Generate'} System Prompt</button>
                  </div>
                  <textarea className="textarea textarea-sm textarea-bordered border-secondary bg-neutral focus-within:outline-none shadow-sm dark:text-white leading-5 h-32" placeholder="What is this Space's main task(s)? How should it behave? What should it avoid doing?" autoComplete="off" maxLength={40000} value={instructions} disabled={isGeneratingSystemPrompt} onChange={(e) => setInstructions(e.target.value)}></textarea>
                </div>
                  
                <div className="form-control w-full mb-4">
                  <div className="label pb-1">
                    <span className="label-text dark:text-white text-sm font-bold">Co-Creator</span>
                  </div>
                  {isEmpty(coCreatorName) ? 
                    <UsersPicker placeholder="Select a Co-Creator" source={1} onChange={handleCoCreatorChange} />
                  : 
                    <div className="flex items-center justify-between border border-[oklch(var(--bc)/0.2)] rounded-lg text-xs gap-2 py-1 px-2">
                      <div>{coCreatorName}</div> 
                      <div>
                        <button type="button" className="btn btn-xs btn-outline btn-error text-white px-1" onClick={() => { setCoCreator(''); setCoCreatorName(''); }}><X size={16} /></button>
                      </div>
                    </div>
                  }
                </div>
              
                <div className="mb-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white px-0" onClick={() => setConvStartersExpanded(!convStartersExpanded)}>
                    {convStartersExpanded ? <ChevronDown size={18} className="mt-0.5" /> : <ChevronRight size={18} className="mt-0.5" />} Conversation Starters
                    <div className="tooltip tooltip-right before:text-xs before:shadow-mdalign-text-top pt-[2px]" data-tip="Quick ways for the user to start a conversation with this Space.">
                      <Info size={16} className="ms-2" />
                    </div>
                  </button>
                  <div className={`transition-all duration-300 overflow-hidden ${convStartersExpanded ? 'opacity-100 px-1 mt-2' : 'max-h-0 opacity-0'}`}>
                    <div className="form-control w-full">
                      <textarea className="textarea textarea-sm textarea-bordered border-secondary bg-neutral focus-within:outline-none shadow-sm dark:text-white leading-5 h-16 mb-2" placeholder="Example prompt #1" autoComplete="off" maxLength={10000} value={convStarter1} onChange={(e) => setConvStarter1(e.target.value)}></textarea>
                      <textarea className="textarea textarea-sm textarea-bordered border-secondary bg-neutral focus-within:outline-none shadow-sm dark:text-white leading-5 h-16 mb-2" placeholder="Example prompt #2" autoComplete="off" maxLength={10000} value={convStarter2} onChange={(e) => setConvStarter2(e.target.value)}></textarea>
                      <textarea className="textarea textarea-sm textarea-bordered border-secondary bg-neutral focus-within:outline-none shadow-sm dark:text-white leading-5 h-16 mb-2" placeholder="Example prompt #3" autoComplete="off" maxLength={10000} value={convStarter3} onChange={(e) => setConvStarter3(e.target.value)}></textarea>
                      <textarea className="textarea textarea-sm textarea-bordered border-secondary bg-neutral focus-within:outline-none shadow-sm dark:text-white leading-5 h-16" placeholder="Example prompt #4" autoComplete="off" maxLength={10000} value={convStarter4} onChange={(e) => setConvStarter4(e.target.value)}></textarea>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white px-0" onClick={() => setOptionsExpanded(!optionsExpanded)}>{optionsExpanded ? <ChevronDown size={18} className="mt-0.5" /> : <ChevronRight size={18} className="mt-0.5" />} Advanced Options</button>
                  <div className={`transition-all duration-300 overflow-hidden ${optionsExpanded ? 'opacity-100 px-1 mt-2' : 'max-h-0 opacity-0'}`}>
                    <div className="form-control w-full mb-4">
                      <div className="label pb-1">
                        <span className="label-text dark:text-white text-sm">
                          <span className="font-bold">Default Model</span>
                          <div className="tooltip tooltip-right before:text-xs before:shadow-md before:mt-4 align-text-top pt-[2px]" data-tip="The model that is selected by default when someone starts chatting with this Space. Users can still switch to a different model.">
                            <Info size={16} className="ms-2" />
                          </div>
                        </span>
                      </div>
                      <Select onValueChange={setModel} value={model}>
                        <SelectTrigger className="select-bordered border-secondary bg-neutral shadow-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-neutral">
                          {chatModels.map((m, idx) => (<SelectItem key={idx} value={m.id}>{m.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>

                    {(files.length > 0 || filesArr.length > 0 || sharepointURLs.length > 0) ?
                      <>
                        <div className="form-control w-full mb-4">
                          <div className="label pb-1">
                            <span className="label-text dark:text-white text-sm font-bold">Documents to Reference
                              <div className="tooltip tooltip-right tooltip-secondary before:text-xs align-text-top pt-[2px]" data-tip="Controls the number of documents to be retrieved when providing a response. (Warning: more documents = more expensive & slower response times)">
                                <Info size={16} className="ms-2" />
                              </div>
                            </span>
                          </div>
                          <input type="range" min="5" max="50" className="range range-xs range-accent" step="5" value={topN} onChange={(e) => setTopN(e.target.value)} />
                          <div className="flex w-full justify-between px-2 text-xs">
                            <span>5</span>
                            <span>10</span>
                            <span>15</span>
                            <span>20</span>
                            <span>25</span>
                            <span>30</span>
                            <span>35</span>
                            <span>40</span>
                            <span>45</span>
                            <span>50</span>
                          </div>
                        </div>

                        <div className="form-control">
                          <label className="label cursor-pointer w-fit">
                            <input type="checkbox" className={`checkbox checkbox-sm ${filesDownloadable ? `checkbox-primary` : ``} me-2`} checked={filesDownloadable} onChange={(e) => setFilesDownloadable(e.target.checked)} />
                            <span className="label-text dark:text-white">Files {filesDownloadable ? <strong>can</strong> : <strong>cannot</strong>} be downloaded by users</span>
                          </label>
                        </div>
                      </>
                    : null}
                  </div>
                </div>

                <div className="divider"></div>

                <div className="dark:text-white text-xl font-bold mb-1">Access</div>
                <div className="text-black/50 dark:text-accent text-sm mb-4">Select who can use this Space</div>

                <div className="form-control w-full">
                  <div className="flex relative bg-neutral rounded-md shadow-sm p-1">
                    <Switch className="min-w-0 basis-0 h-15 text-xs tooltip tooltip-top before:text-xs before:shadow-md before:ms-6" data-tip="This Space will be visible and usable to everyone." checked={visibility == 0} onCheckedChange={() => setVisibility(0)}>
                      <div className="inline-flex items-center dark:text-white text-sm">Public <Info size={16} className="ms-2" /></div>
                    </Switch>
                    <Switch className="min-w-0 basis-0 h-15 text-xs tooltip tooltip-top before:text-xs before:shadow-md" data-tip="This Space will be visible to everyone, but only usable by those that you permit." checked={visibility == 1} onCheckedChange={() => setVisibility(1)}>
                      <div className="inline-flex items-center dark:text-white text-sm">Restricted <Info size={16} className="ms-2" /></div>
                    </Switch>
                    <Switch className="min-w-0 basis-0 h-15 text-xs tooltip tooltip-top before:text-xs before:shadow-md before:-ms-6" data-tip="This Space will be visible and usable only to those that you permit." checked={visibility == 2} onCheckedChange={() => setVisibility(2)}>
                      <div className="inline-flex items-center dark:text-white text-sm">Private <Info size={16} className="ms-2" /></div>
                    </Switch>
                  </div>
                </div>

                {(!isEmpty(gptId) && visibility == 1) &&
                  <div className="flex relative bg-neutral rounded-lg shadow-sm p-1 my-4">
                    <Switch className="flex flex-1 items-center justify-center rounded-md py-3" checked={accessSource === 0} onCheckedChange={() => setAccessSource(0)}>
                      <span className="dark:text-white text-sm">Groups</span>
                    </Switch>
                    <Switch className="flex flex-1 items-center justify-center rounded-md py-3" checked={accessSource === 1} onCheckedChange={() => setAccessSource(1)}>
                      <span className="dark:text-white text-sm">Users</span>
                    </Switch>
                  </div>
                }
                
                {(!isEmpty(gptId) && visibility == 1) && <div className="my-4"><UsersPicker placeholder={accessSource == 0 ? 'Search groups...' : 'Search users...'} source={accessSource} onChange={handleUsersChange} /></div>}

                {/* GROUPS */}
                {(visibility == 1 && accessSource == 0 && (!isEmpty(accessArr) || isReloading)) ?
                  <div className="form-control w-full my-4">
                    <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Groups</span></div>
                    {isReloading ? <div className="text-center"><span className="loading loading-spinner loading-md text-primary"></span></div> : null}
                    {accessArr.filter((a: any) => a.type === 'group').map((access: any) =>
                      <div key={access.id} className="flex items-center justify-between bg-secondary border border-[oklch(var(--bc)/0.2)] rounded-lg text-xs gap-2 py-1 px-2 mb-2" id={`row_${access.id}`}>
                        <div>{access.name}</div> 
                        <div id={`grant_${access.id}`}>
                          <button type="button" className="btn btn-xs btn-error text-white" onClick={() => handleGrantAccess(access.id, -1, 0)}>Revoke</button>
                        </div>
                      </div>
                    )}
                  </div>
                : null}
                
                {/* USERS */}
                {(visibility == 1 && accessSource == 1 && (!isEmpty(accessArr) || isReloading)) ?
                  <div className="form-control w-full my-4">
                    <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Users</span></div>
                    {isReloading ? <div className="text-center"><span className="loading loading-spinner loading-md text-primary"></span></div> : null}
                    {accessArr.filter((a: any) => isEmpty(a.type) || a.type === 'user').map((access: any) =>
                      <div key={access.id} className="flex items-center justify-between bg-secondary border border-[oklch(var(--bc)/0.2)] rounded-lg text-xs gap-2 py-1 px-2 mb-2" id={`row_${access.id}`}>
                        <div>{access.name}</div> 
                        {access.granted ? 
                          <div id={`grant_${access.id}`}>
                            <button type="button" className="btn btn-xs btn-error text-white" onClick={() => handleGrantAccess(access.id, -1)}>Revoke</button>
                          </div>
                        : 
                          <div id={`grant_${access.id}`}>
                            <button type="button" className="btn btn-xs btn-error text-white" onClick={() => handleGrantAccess(access.id, -1)}>Deny</button>
                            <button type="button" className="btn btn-xs btn-success text-white ms-2" onClick={() => handleGrantAccess(access.id, 1)}>Grant</button>
                          </div>
                        }
                      </div>
                    )}
                  </div>
                : null}
              </div>
            </div>

            {/* Right Column */}
            <div className="md:flex-1 md:h-full md:overflow-y-auto scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
              <div className="w-full p-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="dark:text-white text-xl font-bold mb-1">Knowledge</div>
                    <div className="text-black/50 dark:text-accent text-sm">Select the knowledge sources for this Space</div>
                  </div>
                  {(files.length > 0 || filesArr.length > 0 || sharepointURLs.length > 0) ? (!loadingIndexedFiles ? <button type="button" className="btn btn-sm btn-primary btn-outline" onClick={loadIndexedFiles}>View Files</button> : <LoaderCircle className="text-primary animate-spin" size={20} />) : null}
                </div>

                {Object.keys(errors).indexOf('files') > -1 ? <div className="bg-error bg-opacity-25 border border-error border-opacity-50 rounded-lg text-sm text-white py-1 px-2 my-2">{errors.files}</div> : null}
                {Object.keys(errors).indexOf('sharepoint') > -1 ? <div className="bg-error bg-opacity-25 border border-error border-opacity-50 rounded-lg text-sm text-white py-1 px-2 my-2">{errors.sharepoint}</div> : null}
                {Object.keys(errors).indexOf('article') > -1 ? <div className="bg-error bg-opacity-25 border border-error border-opacity-50 rounded-lg text-sm text-white py-1 px-2 my-2">{errors.article}</div> : null}

                {/* SharePoint sync warning summary — visible even while the Document Libraries section below is collapsed */}
                {actionableSharepointCount > 0 ?
                  <div role="alert" className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200 my-2">
                    <TriangleAlert size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <span>
                      {actionableSharepointCount === 1 ? '1 SharePoint document library isn\'t' : `${actionableSharepointCount} SharePoint document libraries aren't`} syncing. Expand SharePoint Document Libraries below for details, fix the affected URL(s), then update.
                    </span>
                  </div>
                : (disabledSharepointCount > 0 ?
                  <div role="status" className="flex items-start gap-2 rounded-lg border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-800 dark:border-sky-400/30 dark:bg-sky-400/10 dark:text-sky-200 my-2">
                    <Info size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
                    <span>
                      {disabledSharepointCount === 1 ? '1 SharePoint URL is' : `${disabledSharepointCount} SharePoint URLs are`} nested under another synced library, so they're covered automatically — it can be removed.
                    </span>
                  </div>
                : null)}

                {/* Files */}
                <div className="bg-neutral border border-secondary rounded-lg shadow-sm p-2 mb-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white w-full no-animation px-0" onClick={() => setFilesExpanded(!filesExpanded)}>{filesExpanded ? <ChevronDown size={18} className="mt-0.5" /> : <ChevronRight size={18} className="mt-0.5" />} Files {filesArr.length > 0 ? ` (${filesArr.length})` : ``}</button>
                  <div className={`transition-all duration-300 overflow-hidden ${filesExpanded ? 'opacity-100 px-1 mt-2' : 'max-h-0 opacity-0'}`}>
                    {/* Uploaded Files */}
                    {!isEmpty(filesArr) ?
                      <div className="flex flex-grow flex-wrap overflow-y-auto scrollbar-none max-w-[50vw] break-all gap-2 mt-2">
                        {filesArr.map((file, idx) => (
                          <div key={idx} className="flex items-center border border-primary/25 rounded-xl text-xs gap-2 p-2">
                            <div className="flex items-center gap-1">
                              <img src={getFileIcon(file)} width="20" />
                              {file}
                            </div>
                            {!isIndexing ? <CircleX size={16} className="text-error hover:text-red-500 rounded-full cursor-pointer" onClick={() => handleDeleteFile(file)} /> : null}
                          </div>
                        ))}
                      </div>
                    : null}

                    {/* Files to Upload */}
                    {!isEmpty(files) ?
                      <>
                        <div className="divider"></div>
                        <div className="flex flex-grow flex-wrap overflow-y-auto scrollbar-none max-w-[50vw] break-all gap-2 mt-2">
                          {files.map((file, idx) => (
                            <div key={idx} className="flex items-center border border-primary/25 rounded-xl text-xs gap-2 p-2">
                              <div className="flex items-center gap-1">
                                <img src={getFileIcon(file.name)} width="20" />
                                {file.name}
                              </div>
                              <CircleX size={16} className="text-error hover:text-red-500 rounded-full cursor-pointer" onClick={() => handleRemoveFile(file)} />
                            </div>
                          ))}
                        </div>
                      </>
                    : null}
                    
                    {!isIndexing ? <button type="button" className="btn btn-xs btn-outline btn-primary mt-6" onClick={() => setKnowledgeFilesDialogOpen(true)}><Plus size={16} /> Add Files</button> : null}
                    {isIndexing ? <div className="flex items-center text-xs text-warning mt-2 gap-2"><span className="loading loading-spinner loading-xs"></span> Knowledge changes are disabled until files are finished processing...</div> : null}
                  </div>
                </div>

                {/* SharePoint Document Library */}
                <div className="bg-neutral border border-secondary rounded-lg shadow-sm p-2 mb-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white w-full no-animation px-0" onClick={() => setSPDocLibExpanded(!spDocLibExpanded)}>{spDocLibExpanded ? <ChevronDown size={18} className="mt-0.5" /> : <ChevronRight size={18} className="mt-0.5" />} SharePoint Document Libraries {sharepointURLs.length > 0 ? ` (${sharepointURLs.length})` : ``}</button>
                  <div className={`transition-all duration-300 overflow-hidden ${spDocLibExpanded ? 'opacity-100 px-1 mt-2' : 'max-h-0 opacity-0'}`}>
                    {sharepointURLs.map((url: string, idx: number) => {
                      const status = sharepointStatus.find(s => s.sharepointURL === url && s.syncDisabled);
                      const isInformational = status?.syncDisabledReason === 'nested_under_other_library';
                      const safeUrl = getSafeSharePointUrl(url);
                      return (
                        <div key={idx} className="max-w-[50vw] border border-primary/50 rounded-xl mt-2 overflow-hidden">
                          <div className="flex items-center justify-between break-all gap-2 p-2">
                            <div className="text-sm">{url}</div>
                            <div className="flex items-center gap-2">
                              {safeUrl ? <a href={safeUrl} target="_blank" rel="noopener noreferrer" aria-label="Open SharePoint document library in a new tab" className="text-primary"><ExternalLink size={18} aria-hidden="true" /></a> : null}
                              {!isIndexing ? <>
                                <button type="button" className="btn btn-sm btn-ghost text-success p-0" onClick={() => { setEditingSPDocLibIndex(idx); setCurrentSPDocLibURL(url); setKnowledgeSPDocLibDialogOpen(true); }}><Pencil size={16} /></button>
                                <button type="button" className="btn btn-sm btn-ghost text-error p-0" onClick={() => setSharepointURLs(sharepointURLs.filter((_, i) => i !== idx))}><X size={18} /></button>
                              </> : null}
                            </div>
                          </div>
                          {status ?
                            <div className={`flex items-start gap-2 text-xs px-2 py-1.5 border-t ${isInformational ? 'bg-sky-500/10 border-sky-500/30 text-sky-800 dark:bg-sky-400/10 dark:border-sky-400/30 dark:text-sky-200' : 'bg-amber-500/10 border-amber-500/30 text-amber-800 dark:bg-amber-400/10 dark:border-amber-400/30 dark:text-amber-200'}`}>
                              {isInformational ? <Info size={14} className="mt-0.5 shrink-0" aria-hidden="true" /> : <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden="true" />}
                              <span><span className="font-bold">{isInformational ? 'Not synced separately: ' : 'Not syncing: '}</span>{getSharepointSyncDisabledMessage(status.syncDisabledReason, status.syncDisabledDetail)}</span>
                            </div>
                          : null}
                        </div>
                      )
                    })}
                    {(!isIndexing && sharepointURLs.length < 5) ? <button type="button" className="btn btn-xs btn-outline btn-primary mt-6" onClick={() => { setEditingSPDocLibIndex(null); setCurrentSPDocLibURL(''); setKnowledgeSPDocLibDialogOpen(true); }}><Plus size={16} /> Add SharePoint Document Library</button> : null}
                    {isIndexing ? <div className="flex items-center text-xs text-warning mt-2 gap-2"><span className="loading loading-spinner loading-xs"></span> Knowledge changes are disabled until files are finished processing...</div> : null}
                  </div>
                </div>

                {/* SharePoint Intranet Articles */}
                <div className="bg-neutral border border-secondary rounded-lg shadow-sm p-2 mb-4">
                  <button type="button" className="btn btn-sm btn-ghost justify-start dark:text-white w-full no-animation px-0" onClick={() => setSPArticlesExpanded(!spArticlesExpanded)}>{spArticlesExpanded ? <ChevronDown size={18} className="mt-0.5" /> : <ChevronRight size={18} className="mt-0.5" />} SharePoint Intranet Articles {sharepointArticles.length > 0 ? ` (${sharepointArticles.length})` : ``}</button>
                  <div className={`transition-all duration-300 overflow-hidden ${spArticlesExpanded ? 'opacity-100 px-1 mt-2' : 'max-h-0 opacity-0'}`}>
                    {/* SharePoint Intranet Articles */}
                    {!isEmpty(sharepointArticles) ?
                      sharepointArticles.map((article: string, idx: number) => {
                        const safeArticleUrl = getSafeSharePointUrl(article);
                        return (
                          <div key={idx} className="flex items-center justify-between max-w-[50vw] border border-primary/50 rounded-xl break-all gap-2 p-2 mt-2">
                            <div className="text-sm">{article}</div>
                            <div className="flex items-center gap-2">
                              {safeArticleUrl ? <a href={safeArticleUrl} target="_blank" rel="noopener noreferrer" aria-label="Open SharePoint article in a new tab" className="text-primary"><ExternalLink size={18} aria-hidden="true" /></a> : null}
                              {!isIndexing ? <>
                                <button type="button" className="btn btn-sm btn-ghost text-success p-0" onClick={() => { setCurrentArticleURL(article); setKnowledgeSPArticlesDialogOpen(true); }}><Pencil size={16} /></button>
                                <button type="button" className="btn btn-sm btn-ghost text-error p-0" onClick={() => setSharepointArticles(sharepointArticles.filter((a: string) => a !== article))}><X size={18} /></button>
                              </> : null}
                            </div>
                          </div>
                        );
                      })
                    : null}
                    
                    {(!isIndexing && sharepointArticles.length < 30) ? <button type="button" className="btn btn-xs btn-outline btn-primary mt-6" onClick={() => setKnowledgeSPArticlesDialogOpen(true)}><Plus size={16} /> Add SharePoint Intranet Articles</button> : null}
                    {isIndexing ? <div className="flex items-center text-xs text-warning mt-2 gap-2"><span className="loading loading-spinner loading-xs"></span> Knowledge changes are disabled until files are finished processing...</div> : null}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* DUPLICATES CHECK / SAVING */}
          <Dialog open={isSaving} onOpenChange={setIsSaving}>
            <DialogContent className={`bg-secondary border-transparent rounded-3xl shadow-short [&>button:not(.btn)]:hidden max-h-[calc(100vh-4rem)] p-0 overflow-hidden ${(!isEmpty(similar) ? `max-w-full w-full lg:w-3/4` : `sm:max-w-lg`)}`} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} aria-describedby="save-status-description">
              {!isEmpty(similar) ? (
                <>
                  <DialogHeader className="px-6 pt-6 pb-4 border-b border-base-content/10">
                    <DialogTitle className="flex items-start gap-4 text-left">
                      <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-warning/15 text-warning">
                        <Info size={18} />
                      </div>
                      <div>
                        <div className="text-2xl font-semibold text-base-content">Possible duplicates found</div>
                        <div className="mt-1 text-sm font-normal text-base-content/65">
                          These Spaces may already cover a similar use case. Review them before creating another one.
                        </div>
                      </div>
                    </DialogTitle>
                  </DialogHeader>

                  <div className="max-h-[70vh] overflow-y-auto px-6 py-5 scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
                    <div className="flex flex-col gap-3">
                      {similar.map((s: any, idx: number) => (
                        <div key={idx} className="rounded-2xl border border-base-content/10 bg-base-100/70 px-4 py-3">
                          <div className="flex items-start justify-between gap-4">
                            <div className="min-w-0">
                              <button type="button" className="text-left text-base font-semibold text-primary hover:underline" onClick={() => window.open(`/spaces/v/${s.id}`, '_blank')}>
                                {s.name}
                              </button>
                              <div className="mt-2 text-sm leading-6 text-base-content/75">{s.description}</div>
                            </div>
                            <a href={`/spaces/v/${s.id}`} target="_blank" rel="noreferrer" className="mt-0.5 shrink-0 text-primary">
                              <ExternalLink size={16} />
                            </a>
                          </div>
                          <div className="mt-3 text-xs uppercase tracking-[0.1em] text-base-content/45">Created by</div>
                          <div className="mt-1 text-sm font-medium text-base-content/80">{s.createdByName}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <DialogFooter className="border-t border-base-content/10 px-6 py-4 sm:justify-between">
                    <button type="button" className="btn btn-ghost" onClick={() => { setSimilar([]); isSavingRef.current = false; setIsSaving(false); setCanClose(true); }}>Cancel</button>
                    <button type="button" className="btn btn-warning" onClick={() => { setSimilar([]); handleSave(true); }}>Create anyway</button>
                  </DialogFooter>
                </>
              ) : (
                <>
                  <DialogHeader>
                    <DialogTitle className="sr-only">Save status</DialogTitle>
                  </DialogHeader>

                  <div className="px-6 pb-6 mt-2">
                    <div className={`rounded-3xl border bg-base-100/60 px-6 py-7 text-center ${saveErrorSeverity === 'error' ? 'border-error/30' : saveErrorSeverity === 'warning' ? 'border-warning/30' : 'border-base-content/10'}`}>
                      <div className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${saveErrorSeverity === 'error' ? 'bg-error/15 text-error' : saveErrorSeverity === 'warning' ? 'bg-warning/15 text-warning' : canClose ? 'bg-success/15 text-success' : 'bg-primary/10 text-primary'}`}>
                        {saveErrorSeverity === 'error' ? <CircleX size={34} /> : saveErrorSeverity === 'warning' ? <TriangleAlert size={34} /> : canClose ? <CheckCircle size={32} /> : <span className="loading loading-spinner loading-md"></span>}
                      </div>
                      <div className="mt-4 text-2xl font-semibold text-base-content">
                        {isChecking ? 'Checking for duplicates' : saveErrorSeverity === 'error' ? 'Save failed' : saveErrorSeverity === 'warning' ? 'Knowledge is still processing' : (!canClose ? 'Saving Space' : 'Saved successfully')}
                      </div>
                      <div id="save-status-description" className="mt-2 text-sm leading-6 text-base-content/65">
                        {isChecking
                          ? 'We are comparing the name and description against existing Spaces.'
                          : saveError
                            ? saveError
                            : (!canClose
                            ? 'Your configuration and knowledge sources are being saved now.'
                            : 'Your configuration has been saved.')}
                      </div>

                      {saveErrorDetails ? (
                        <ErrorDetails details={saveErrorDetails} />
                      ) : null}

                      {!saveError && (hoursWarning || sharepointURLs.length > 0) ? (
                        <div className="mt-5 rounded-xl border border-warning/35 bg-warning/10 px-4 py-3 text-left">
                          <div className="text-xs font-semibold uppercase tracking-[0.1em] text-warning">Processing time</div>
                          <div className="mt-1 text-sm leading-5 text-base-content/80">
                            {isEmpty(gptId)
                              ? 'Knowledge ingestion and processing can take up to 24 hours. You will be emailed when it is ready.'
                              : 'Knowledge changes can take up to 24 hours to be ingested and processed.'}
                          </div>
                        </div>
                      ) : null}

                      {canClose ? (
                        <div className="mt-6 flex justify-center">
                          <button type="button" className="btn btn-secondary" onClick={() => {
                            if (saveError) {
                              isSavingRef.current = false;
                              setIsSaving(false);
                            } else navigate('/spaces');
                          }}>
                            {saveError ? 'Return to editor' : 'Close'}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </>
              )}
            </DialogContent>
          </Dialog>

          {/* DELETING */}
          <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
            <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short [&>button:not(.btn)]:hidden sm:max-w-md p-0 overflow-hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="sr-only">Delete status</DialogTitle>
              </DialogHeader>

              <div className="px-6 pb-6 mt-2">
                <div className="rounded-3xl border border-error/20 bg-base-100/60 px-6 py-7 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-error/12 text-error">
                    <span className="loading loading-spinner loading-md text-error"></span>
                  </div>
                  <div className="mt-4 text-2xl font-semibold text-base-content">Deleting Space</div>
                  <div className="mt-2 text-sm leading-6 text-base-content/65">
                    Removing the Space and its configuration. This window will close automatically when the action completes.
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* INDEXED FILES */}
          <Dialog open={indexedFilesDialogOpen} onOpenChange={setIndexedFilesDialogOpen}>
            <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short max-h-[calc(100vh-4rem)] max-w-full w-full lg:w-2/3 p-0 overflow-hidden" aria-describedby={undefined}>
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-base-content/10">
                <DialogTitle className="text-2xl">Indexed Files</DialogTitle>
              </DialogHeader>

              <div className="flex min-h-0 flex-col gap-3 px-6">
                <div className="text-xs text-base-content/65">
                  {indexedFiles.length} {indexedFiles.length === 1 ? 'file' : 'files'}
                  {indexedFiles.some((file) => file.size != null) ? ` · ${formatBytes(indexedFiles.reduce((total, file) => total + (file.size ?? 0), 0))} total` : ''}
                </div>

                {indexedFiles.length > 0 ? (
                  <div className="max-h-[50vh] overflow-y-auto rounded-2xl border border-base-content/10 bg-base-100/60">
                    <table className="table table-sm table-pin-rows">
                      <thead>
                        <tr className="bg-base-200">
                          <th>File</th>
                          <th className="w-28 text-right">Size</th>
                        </tr>
                      </thead>
                      <tbody>
                        {indexedFiles.map((file) => (
                          <tr key={file.filepath}>
                            <td>
                              <div className="flex items-center gap-2 break-all">
                                <img src={getFileIcon(file.filepath)} width="18" className="shrink-0" />
                                {file.filepath}
                              </div>
                            </td>
                            <td className="whitespace-nowrap text-right tabular-nums text-base-content/70">
                              {file.size != null ? formatBytes(file.size) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-base-content/10 bg-base-100/60 px-4 py-6 text-sm text-base-content/55 text-center">
                    No indexed files yet.
                  </div>
                )}
              </div>

              <DialogFooter className="border-t border-base-content/10 px-6 py-4">
                <button type="button" className="btn btn-primary btn-outline" onClick={exportIndexedFiles} disabled={indexedFiles.length === 0}>
                  <Download size={16} /> Download to Excel
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ADD FILES */}
          <Dialog open={knowledgeFilesDialogOpen} onOpenChange={setKnowledgeFilesDialogOpen}>
            <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short max-h-[calc(100vh-4rem)] min-h-96 max-w-full w-full lg:w-2/3 p-0 overflow-hidden" aria-describedby={undefined}>
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-base-content/10">
                <DialogTitle className="text-2xl">Add Files</DialogTitle>
              </DialogHeader>

              <div className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto px-6">
                <div className="rounded-xl border border-warning/40 bg-warning/12 px-3 py-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-warning/20 text-warning">
                      <Info size={15} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-warning">Size limits</div>
                      <div className="mt-1 text-sm font-medium leading-5 text-base-content">
                        Individual files can be up to 250MB, with a combined limit of 5GB shared across uploaded files and SharePoint Document Libraries.{existingStorageBytes > 0 ? ` This Space has ${(Math.max(0, STORAGE_LIMIT_BYTES - existingStorageBytes) / (1024 * 1024)).toFixed(0)}MB remaining; anything beyond that is not added.` : ''}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-base-content/10 bg-base-100/60 p-4">
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-base-content">Upload files</div>
                    <Dropzone accept={fileTypes} onDrop={acceptedFiles => handleKnowledgeFileChange(acceptedFiles)} disabled={!isReady || isSaving || isPreparingKnowledgeFiles}>
                      {({getRootProps, getInputProps}) => (
                        <section>
                          <div
                            className={`flex h-36 flex-col items-center justify-center rounded-xl border border-dashed border-[oklch(var(--bc)/0.2)] bg-base-100/70 px-6 text-center transition-colors ${isReady ? `cursor-pointer hover:border-primary/40 hover:bg-base-100` : `opacity-60`}`}
                            {...getRootProps()}
                          >
                            <input {...getInputProps()} />
                            <div className="text-sm font-medium text-base-content">Drag and drop files here</div>
                            <div className="mt-1 text-xs text-base-content/65">or click to browse from your device</div>
                          </div>
                        </section>
                      )}
                    </Dropzone>
                  </div>
                  {isPreparingKnowledgeFiles ? <div role="status" className="mt-2 text-xs text-base-content/65">Preparing selected files…</div> : null}
                </div>

                <div className="rounded-2xl border border-base-content/10 bg-base-100/50 p-4">
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div className="text-sm font-medium text-base-content">Selected files</div>
                    <div className="text-xs text-base-content/65">{files.length} {files.length === 1 ? 'file' : 'files'}</div>
                  </div>

                  <div className="flex min-h-0 flex-wrap gap-2">
                    {files.length > 0 ? files.map((file, idx) => (
                      <div key={idx} className="flex items-center border border-primary/25 rounded-xl text-xs gap-2 p-2">
                        <div className="flex items-center gap-1 break-all">
                          <img src={getFileIcon(file.name)} width="20" />
                          {file.name}
                        </div>
                        <CircleX size={16} className="text-error hover:text-red-500 rounded-full cursor-pointer shrink-0" onClick={() => handleRemoveFile(file)} />
                      </div>
                    )) : (
                      <div className="rounded-xl border border-dashed border-base-content/10 bg-base-100/60 px-4 py-6 text-sm text-base-content/55 w-full text-center">
                        No files selected yet.
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <DialogFooter className="border-t border-base-content/10 px-6 py-4">
                <button type="button" className="btn btn-success text-white" onClick={() => setKnowledgeFilesDialogOpen(false)}>Add</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ADD / EDIT SHAREPOINT DOCUMENT LIBRARY */}
          <Dialog open={knowledgeSPDocLibDialogOpen} onOpenChange={setKnowledgeSPDocLibDialogOpen}>
            <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short max-h-[calc(100vh-4rem)] max-w-full w-full lg:w-2/3 p-0 overflow-hidden" aria-describedby={undefined}>
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-base-content/10">
                <DialogTitle className="text-2xl">{editingSPDocLibIndex !== null ? 'Edit' : 'Add'} SharePoint Document Library</DialogTitle>
              </DialogHeader>

              <div className="flex flex-col gap-4 px-6">
                <div className="rounded-xl border border-warning/40 bg-warning/12 px-3 py-3">
                  <div className="flex items-start gap-3">
                    <div className="mt-0.5 flex h-7 w-7 items-center justify-center rounded-full bg-warning/20 text-warning">
                      <Info size={15} />
                    </div>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.1em] text-warning">Size limits</div>
                      <div className="mt-1 text-sm font-medium leading-5 text-base-content">
                        Individual files can be up to 250MB, with a combined limit of 5GB shared across uploaded files and SharePoint Document Libraries.{existingStorageBytes > 0 ? ` This Space has ${(Math.max(0, STORAGE_LIMIT_BYTES - existingStorageBytes) / (1024 * 1024)).toFixed(0)}MB remaining; anything beyond that is not added.` : ''}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-base-content/10 bg-base-100/60 p-4">
                  <div className="space-y-3">
                    <label className="block">
                      <div className="mb-2 text-sm font-medium text-base-content">Library URL</div>
                      <input
                        type="text"
                        className="input input-bordered w-full bg-transparent text-sm focus-within:outline-none"
                        placeholder="https://smsgroupgmbh.sharepoint.com/teams/example/Shared%20Documents..."
                        value={currentSPDocLibURL}
                        onChange={(e) => setCurrentSPDocLibURL(e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <DialogFooter className="border-t border-base-content/10 px-6 py-4">
                <button type="button" className="btn btn-success text-white" onClick={() => {
                  const trimmed = currentSPDocLibURL.trim();
                  if (!trimmed) { setKnowledgeSPDocLibDialogOpen(false); return; }
                  if (editingSPDocLibIndex !== null) {
                    setSharepointURLs(sharepointURLs.map((u, i) => i === editingSPDocLibIndex ? trimmed : u));
                  } else {
                    if (!sharepointURLs.includes(trimmed)) setSharepointURLs([...sharepointURLs, trimmed]);
                  }
                  setCurrentSPDocLibURL('');
                  setEditingSPDocLibIndex(null);
                  setKnowledgeSPDocLibDialogOpen(false);
                }}>{editingSPDocLibIndex !== null ? 'Save' : 'Add'}</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* ADD SHAREPOINT INTRANET ARTICLES */}
          <Dialog open={knowledgeSPArticlesDialogOpen} onOpenChange={setKnowledgeSPArticlesDialogOpen}>
            <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short max-h-[calc(100vh-4rem)] max-w-full w-full lg:w-2/3 p-0 overflow-hidden" aria-describedby={undefined}>
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-base-content/10">
                <DialogTitle className="text-2xl">SharePoint Intranet Articles</DialogTitle>
              </DialogHeader>

              <div className="flex flex-col gap-4 px-6">
                <div className="rounded-2xl border border-base-content/10 bg-base-100/50 px-4 py-2">
                  <div className="flex items-center gap-3">
                    <div className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-info/15 text-info">
                      <Info size={16} />
                    </div>
                    <div className="space-y-3 text-sm">
                      <div className="text-xs leading-5 text-base-content/65">
                        You can add up to 30 article links. Keep each one scoped to a concrete page to avoid noisy knowledge sources.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="rounded-2xl border border-base-content/10 bg-base-100/60 p-4">
                  <div className="space-y-3">
                    <label className="block">
                      <div className="mb-2 text-sm font-medium text-base-content">Article URL</div>
                      <input
                        type="text"
                        className="input input-bordered w-full bg-transparent text-sm focus-within:outline-none"
                        placeholder="https://smsgroupgmbh.sharepoint.com/sites/example/SitePages/Article..."
                        value={currentArticleURL}
                        onChange={(e) => setCurrentArticleURL(e.target.value)}
                      />
                    </label>
                  </div>
                </div>
              </div>

              <DialogFooter className="border-t border-base-content/10 px-6 py-4">
                <button type="button" className="btn btn-success text-white" onClick={() => {
                  const trimmedArticle = currentArticleURL.trim();
                  if (!isEmpty(trimmedArticle) && !sharepointArticles.includes(trimmedArticle)) {
                    setSharepointArticles([...sharepointArticles, trimmedArticle]);
                    setCurrentArticleURL('');
                  }
                  setKnowledgeSPArticlesDialogOpen(false);
                }}>Add</button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      }
    </div>
  );
}

export default EditCustomGPT;
